/**
 * The pale band the painter laid along the shores (STYLE §7), mirrored by shoreBand.glsl.
 *
 * Measured on the museum's scan of the painting (docs/reference/painting-1538-amsterdam-museum.jpg,
 * area-averaged to the 2000 px frame, whose 4:2:0 chroma is 3.3 times finer than the 2000 px copy's):
 * the painted water was told from the painted land by colour, and every water pixel's distance to
 * the nearest land pixel taken in metres on the water surface through the painter rays
 * (tools/fields/painterRays.ts). Against the same body's water further out in the same 25 px row
 * band, the water within a few metres of a shore is yellower and less green, its lightness the same
 * within 0.8 L*, and the shift falls off as e^(-d / w):
 * - the IJ within 60 m of the site's shores, against its water 30-60 m out, 70,000 pixels at
 *   1-30 m: a* +1.85, b* +5.00, w 8.1 m (rms 0.12). Only there: out on the IJ, the water about
 *   the ships and the palisade takes a like halo (a* +2.40, b* +4.26, w 8.6 m), no shore's;
 * - the moats, canals and the Amstel against each one's middle water (beyond 70% of its pixels'
 *   distances), 47,000 pixels at 1-22 m: a* +2.99, b* +4.47, w 4.2 m (rms 0.57).
 * The first metre, where a bank's colour may run into the paint, is left out of both fits. Bodies
 * of different colours take much the same shift, so the band is a shift in Lab, not a colour.
 */
import type { Lab, LinearRGB } from '../core/color';
import { linearToLab } from '../core/color';

export interface ShoreBandFit {
  /** The shift in a* and b* at the shore; lightness is kept. */
  readonly shift: readonly [number, number];
  /** The distance over which the shift falls by e, in metres. */
  readonly widthM: number;
}

export const SHORE_BAND = {
  /** The IJ and the harbour: water.frag's open water. */
  open: { shift: [1.85, 5.0], widthM: 8.1 },
  /** Every other kind: the painting's moats, canals and rivers. */
  channel: { shift: [2.99, 4.47], widthM: 4.2 },
  /** Beyond it the band is gone: 4.9 widths of the open water's, where it had fallen to 0.7%. */
  reachM: 40,
} as const satisfies {
  open: ShoreBandFit;
  channel: ShoreBandFit;
  reachM: number;
};

const WHITE = [0.95047, 1.0, 1.08883] as const;
const XYZ_TO_LINEAR = [
  [3.2404542, -1.5371385, -0.4985314],
  [-0.969266, 1.8760108, 0.041556],
  [0.0556434, -0.2040259, 1.0572252],
] as const;

/* core/color.ts xyzToLab, inverted. */
function labChannelToXyz(f: number): number {
  const cube = f * f * f;
  return cube > 0.008856 ? cube : (f - 16 / 116) / 7.787;
}

/** The linear colour of a Lab colour, the inverse of linearToLab. */
export function labToLinear(lab: Lab): LinearRGB {
  const fy = (lab[0] + 16) / 116;
  const xyz = [fy + lab[1] / 500, fy, fy - lab[2] / 200].map(
    (f, c) => labChannelToXyz(f) * WHITE[c]!
  );
  const [r, g, b] = XYZ_TO_LINEAR.map(
    (row) => row[0] * xyz[0]! + row[1] * xyz[1]! + row[2] * xyz[2]!
  );
  return [r!, g!, b!];
}

/** How much of the shift a point this far from the shore takes: e^(-d / w), brought to 0 at the
 *  reach so the band ends there without a step. */
export function shoreBandWeight(
  distanceM: number,
  widthM: number,
  reachM: number = SHORE_BAND.reachM
): number {
  const cut = Math.exp(-reachM / widthM);
  return Math.max(Math.exp(-distanceM / widthM) - cut, 0) / (1 - cut);
}

/** The colour with the band's shift laid on it by the weight. */
export function shoreBandColour(colour: LinearRGB, weight: number, fit: ShoreBandFit): LinearRGB {
  if (weight <= 0) return colour;
  const [l, a, b] = linearToLab(colour);
  return labToLinear([l, a + fit.shift[0] * weight, b + fit.shift[1] * weight]);
}
